import React, { useEffect, useMemo, useState } from "react";
import { IoAddCircleOutline, IoRemoveCircleOutline, IoSearch } from "react-icons/io5";
import Sheet from "../../components/layout/app/Sheet";
import { Section } from "./parts";

const NOT_ASSIGNED = "Not assigned";
const keyOf = (applianceId) => String(applianceId);

// Devices by floor, then by room, in the order the property lists them.
const groupDevices = (devices) => {
  const floors = new Map();
  devices.forEach((device) => {
    const floorKey = String(device.floorId ?? device.floor ?? NOT_ASSIGNED);
    if (!floors.has(floorKey)) floors.set(floorKey, { key: floorKey, name: device.floor || NOT_ASSIGNED, rooms: new Map() });
    const { rooms } = floors.get(floorKey);
    const roomKey = String(device.roomId ?? device.room ?? NOT_ASSIGNED);
    if (!rooms.has(roomKey)) rooms.set(roomKey, { key: roomKey, name: device.room || NOT_ASSIGNED, devices: [] });
    rooms.get(roomKey).devices.push(device);
  });
  return [...floors.values()].map((floor) => ({ ...floor, rooms: [...floor.rooms.values()] }));
};

// The app's Select Devices screen (app/screens/ShareProperty/DeviceSelectScreen.js
// and SearchDevicesChange.js): a search box, the property's devices by floor and
// room, and a button on each to add it to the share or take it out.
// devices is null while they load. onSave gets the chosen applianceIds.
const DevicePicker = ({ open, devices, selected, onCancel, onSave }) => {
  const [picked, setPicked] = useState(() => new Set());
  const [term, setTerm] = useState("");

  useEffect(() => {
    if (!open) return;
    setPicked(new Set(selected.map(keyOf)));
    setTerm("");
    // Starts from the current choice each time it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const groups = useMemo(() => {
    const text = term.trim().toLowerCase();
    const shown = (devices ?? []).filter((device) => !text || (device.name ?? "").toLowerCase().includes(text));
    return groupDevices(shown);
  }, [devices, term]);

  const toggle = (device) =>
    setPicked((current) => {
      const next = new Set(current);
      const key = keyOf(device.applianceId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Ids go back as the property lists them. Ones it no longer has are dropped.
  const save = () =>
    onSave(devices.filter((device) => picked.has(keyOf(device.applianceId))).map((device) => device.applianceId));

  return (
    <Sheet open={open} onClose={onCancel} title="Select Devices">
      <div className="hx-share-picker">
        <div className="hx-share-search">
          <IoSearch aria-hidden="true" />
          <input
            className="hx-input"
            type="search"
            placeholder="Search for devices..."
            aria-label="Search for devices"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>

        {devices === null ? (
          <div className="hx-share-loading" aria-label="Loading devices">
            {[0, 1, 2].map((index) => (
              <div key={index} className="hx-skel" />
            ))}
          </div>
        ) : groups.length ? (
          groups.map((floor, floorIndex) => (
            <Section key={floor.key} id={`hx-share-floor-${floorIndex}`} title={floor.name} as="h3" className="is-floor">
              {floor.rooms.map((room, roomIndex) => (
                <Section
                  key={room.key}
                  id={`hx-share-room-${floorIndex}-${roomIndex}`}
                  title={room.name}
                  as="h4"
                  className="is-room"
                >
                  <ul className="hx-share-devices">
                    {room.devices.map((device) => {
                      const added = picked.has(keyOf(device.applianceId));
                      return (
                        <li key={keyOf(device.applianceId)} className="hx-share-device">
                          <span className="hx-share-device-text">
                            <span className="hx-share-device-name">{device.name}</span>
                            <span className="hx-share-device-place">
                              {device.floor || NOT_ASSIGNED} | {device.room || NOT_ASSIGNED}
                            </span>
                          </span>
                          <button
                            type="button"
                            className={`hx-share-toggle ${added ? "is-added" : ""}`}
                            aria-pressed={added}
                            aria-label={`Share ${device.name}`}
                            onClick={() => toggle(device)}
                          >
                            {added ? <IoRemoveCircleOutline aria-hidden="true" /> : <IoAddCircleOutline aria-hidden="true" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </Section>
              ))}
            </Section>
          ))
        ) : (
          <p className="hx-share-none">
            {devices.length ? `No devices match "${term.trim()}".` : "This property has no devices yet."}
          </p>
        )}

        <div className="hx-share-picker-foot">
          <button type="button" className="hx-share-btn is-danger" onClick={onCancel}>
            CANCEL
          </button>
          <button type="button" className="hx-share-btn" onClick={save} disabled={devices === null}>
            SAVE
          </button>
        </div>
      </div>
    </Sheet>
  );
};

export default DevicePicker;
